import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, SectionLabel } from "./Reveal";

const CRAFT_IMG = "/raine.webp";

const CHAPTERS = [
  {
    n: "01",
    title: "Preparation is everything",
    body: "Two-thirds of every After Paint job happens before colour ever touches the wall. Sanding, filling, sealing, priming — the unglamorous hours that make the final coat look inevitable.",
    testid: "craft-chapter-1",
  },
  {
    n: "02",
    title: "Particular, not fussy",
    body: "Edges cut by hand. Lines you could set a ruler to. Craig is particular — his word for caring about the half-millimetre most people never notice, until they walk into the room.",
    testid: "craft-chapter-2",
  },
  {
    n: "03",
    title: "On time, every time",
    body: "When Craig says Tuesday, it's Tuesday. Quotes are honoured, drop-sheets are laid, and the only trace left behind when he goes is the finish itself.",
    testid: "craft-chapter-3",
  },
];

export default function Manifesto() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  return (
    <section
      id="craft"
      ref={ref}
      data-testid="manifesto-section"
      className="py-24 md:py-32 px-6 md:px-12 lg:px-24"
    >
      <div className="grid lg:grid-cols-12 gap-16 lg:gap-12">
        <div className="lg:col-span-7">
          <Reveal>
            <SectionLabel>A Craft That's Perfected</SectionLabel>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif font-light tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-8">
              The finish is made
              <br />
              <span className="italic text-smoke">before the tin opens.</span>
            </h2>
          </Reveal>

          <div className="mt-20 space-y-0">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.08}>
                <div
                  data-testid={c.testid}
                  className="grid grid-cols-[auto,1fr] gap-8 md:gap-12 py-12 border-t border-line group"
                >
                  <span className="font-serif font-light text-6xl md:text-7xl text-outline-ember leading-none select-none transition-all duration-500 group-hover:[-webkit-text-stroke-color:rgba(252,153,7,1)]">
                    {c.n}
                  </span>
                  <div>
                    <h3 className="font-serif text-2xl md:text-3xl font-normal tracking-tight">
                      {c.title}
                    </h3>
                    <p className="text-smoke font-light leading-relaxed mt-4 max-w-xl text-base">
                      {c.body}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <Reveal delay={0.15}>
              <div className="relative overflow-hidden border border-line">
                <motion.img
                  src={CRAFT_IMG}
                  alt="Freshly painted home ready for sale — After Paint finished work"
                  className="w-full aspect-[4/3] object-cover will-change-transform scale-105"
                  style={{ y: imgY }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-coal/60 to-transparent" />
                <p className="absolute bottom-5 left-5 text-[10px] uppercase tracking-[0.3em] text-bone/70">
                  A fresh after, delivered
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
